import React, { useCallback, useEffect, useState } from 'react';
import { message, Popconfirm, Table, TableProps } from 'antd';
import { TableParams } from '../../../../model/request';
import { LearningBook } from '../../../../model/response';
import { deleteLearningBook, getPageLearningBook } from '../../../../service/learningBookService';
import Search from 'antd/es/input/Search';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import processError from '../../../../utils/processError';

function LearningBookManagement() {
  const [fetching, setFetching] = useState(false);
  const [learningBooks, setLearningBooks] = useState<LearningBook[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const navigate = useNavigate();

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      defaultPageSize: 10,
    },
  });

  const handleLearningBookPage: TableProps['onChange'] = (pagination, filters) => {
    setTableParams({
      pagination,
      filters,
    });
  };

  const fetchData = useCallback(() => {
    setFetching(true);
    getPageLearningBook(tableParams, keyword)
      .then((learningBookPage) => {
        setLearningBooks(learningBookPage.content);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: learningBookPage.totalElements,
          },
        });
      })
      .catch((error) => console.error(error))
      .finally(() => setFetching(false));
  }, [keyword, tableParams]);

  useEffect(() => {
    if (tableParams.pagination) {
      setTableParams({
        pagination: {
          ...tableParams.pagination,
          current: 1,
        },
        filters: tableParams.filters,
      });
    }
    fetchData();
  }, [tableParams.pagination?.pageSize, keyword]);

  useEffect(() => {
    fetchData();
  }, [tableParams.pagination?.current]);

  const learningBookTableColumns: TableProps<LearningBook>['columns'] = [
    {
      title: 'STT',
      dataIndex: 'key',
      render: (_, learningBook, index) => <div>{index + 1}</div>,
    },
    {
      title: 'Tên sách',
      key: 'name',
      render: (_, learningBook) => <div className={'font-bold text-0B7BFF'}>{learningBook.name}</div>,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Hành động',
      key: 'doTest',
      align: 'center',
      render: (_, learningBook) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => handleNavigateUpdateLearningBook(learningBook)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa sách"
            description="Xác nhận xóa sách. Hành động không thể hoàn tác?"
            onConfirm={() => handleDeleteLearningBook(learningBook)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined className={'text-white'} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleDeleteLearningBook = async (learningBook: LearningBook) => {
    try {
      await deleteLearningBook(learningBook.id);
      fetchData();
      message.success('Xoá sách thành công');
    } catch (e) {
      processError(e);
    }
  };

  function handleNavigateUpdateLearningBook(learningBook: LearningBook) {
    navigate(`/learning-assets/learning-books/update/${learningBook.id}`);
  }

  function handleNavigateAddLearningBook() {
    navigate('/learning-assets/learning-books/create');
  }

  return (
    <div className={'w-full h-full p-4'}>
      <div className={'p-2 flex items-center justify-between bg-white rounded'}>
        <div className={'flex gap-4'}>
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={handleNavigateAddLearningBook}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
        </div>
        <Search
          placeholder={'Nhập từ khóa tìm kiếm'}
          rootClassName={'h-9 rounded-3xl border border-solid border-f46055 flex items-center overflow-hidden w-fit'}
          onSearch={setKeyword}
        />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          dataSource={learningBooks}
          columns={learningBookTableColumns}
          rowKey={(learningBook) => learningBook.id}
          onChange={handleLearningBookPage}
          loading={fetching}
          pagination={tableParams.pagination}
        />
      </div>
    </div>
  );
}

export default LearningBookManagement;
